import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../../hooks/useDebounce";
import { useI18n } from "../../../hooks/useTranslations";
import useItemApi from "../../../http/item/api";
import { Item } from "../../../http/item/types";
import { getItemDescription } from "../../../utils";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import Numeric from "../../ui/Numeric";
import reactSelectStyles from "../../ui/ReactSelectUtils";
import Sidebar from "../../ui/Sidebar";
import styles from "./SideBar.module.css";

export type SideBarBody = {
  initialItem: Item | null;
  initialQuantity: number | null;
};

type SideBarProps = {
  isSideBarOpen: boolean;
  onSave: (formBody: SideBarBody) => void;
  onClose: () => void;
  item?: Item | null;
  alreadyPresentItemIds: string[];
  quantity?: number;
};

const SideBar: FC<SideBarProps> = ({ item, alreadyPresentItemIds, quantity, isSideBarOpen, onClose, onSave }) => {
  const i18n = useI18n();

  const { searchItems } = useItemApi();
  const [title, setTitle] = useState(i18n.t.common.addComponent);

  const [defaultItemsOptions, setDefaultItemsOptions] = useState<Item[]>([]);

  return (
    <Sidebar title={title} onClose={onClose} isOpen={isSideBarOpen}>
      <Form<SideBarBody> submitText={i18n.t.common.save} onSubmit={() => onSave}>
        {({ control, reset, setValue }) => {
          useEffect(() => {
            reset({
              initialItem: item,
              initialQuantity: quantity,
            });
          }, [item?.code, quantity]);

          useEffect(() => {
            !isSideBarOpen &&
              reset({
                initialItem: null,
                initialQuantity: null,
              });
          }, [isSideBarOpen]);

          useEffect(() => {
            isSideBarOpen && setTitle(item ? i18n.t.common.edit : i18n.t.common.addComponent);
          }, [isSideBarOpen, item]);

          return (
            <div className={styles.sideBarForm}>
              <Controller
                name={`initialItem`}
                rules={{
                  required: true,
                  validate: (currentItem) => {
                    if (!item && currentItem && alreadyPresentItemIds.includes(currentItem.id)) {
                      return i18n.t.items.errors.alreadyAdded;
                    }
                    return undefined;
                  },
                }}
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.initialItem} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<Item>(aborter, searchTerm, searchItems);

                      useEffect(() => {
                        !item && loadOptions(searchTerm.current).then(setDefaultItemsOptions);
                      }, []);

                      return (
                        <AsyncSelect<Item>
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultItemsOptions}
                          loadOptions={(st) => loadOptions(st)}
                          getOptionLabel={(o) => `${getItemDescription(o, i18n.language)} - ${o.code}`}
                          getOptionValue={(o) => o.id}
                          placeholder={i18n.t.common.select + "..."}
                          isDisabled={!!item}
                          styles={reactSelectStyles(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              <Controller
                name={`initialQuantity`}
                control={control}
                rules={{
                  required: true,
                  min: 1,
                }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.initialItemQuantity} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onBlur={(value) => setValue(`initialQuantity`, value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </div>
          );
        }}
      </Form>
    </Sidebar>
  );
};

export default SideBar;
